import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import Sidebar from "../components/Sidebar";
import config from '../config';

export default function MidwifeClientList() {
  const { user } = useAuth();
  const midwifeId = user?.sub;
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [sortBy, setSortBy] = useState("dueDate");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!midwifeId) return;
    const fetchClients = async () => {
      try {
        const token = localStorage.getItem('velaToken');
        if (!token) {
          setError("Not authenticated");
          return;
        }

        const res = await fetch(`${config.api.url}/midwives/${midwifeId}/clients`, {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        });
        
        if (!res.ok) {
          if (res.status === 401) {
            setError("Session expired. Please log in again.");
            // Optionally redirect to login
            // navigate('/login');
            return;
          }
          throw new Error("Failed to fetch clients.");
        }
        
        const data = await res.json();
        setClients(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchClients();
  }, [midwifeId, navigate]);

  useEffect(() => {
    let sorted = [...clients];
    if (sortBy === "name") {
      sorted.sort((a, b) => a.fullName.localeCompare(b.fullName));
    } else if (sortBy === "dueDate") {
      sorted.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
    }
    if (searchTerm) {
      sorted = sorted.filter(c =>
        c.fullName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    setFilteredClients(sorted);
  }, [clients, sortBy, searchTerm]);

  if (!midwifeId) return <div className="p-4 text-red-600">User not authenticated.</div>;
  if (loading) return <div className="p-4">Loading clients...</div>;
  if (error) return <div className="p-4 text-red-600">Error: {error}</div>;

  return (
    <Sidebar>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h2 className="text-2xl font-bold text-emerald-700">Your Clients</h2>
          <button
            onClick={() => navigate("/midwife")}
            className="w-full sm:w-auto text-sm bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded shadow"
          >
            Back to Dashboard
          </button>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
          <input
            type="text"
            placeholder="Search by name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 px-4 py-2 border rounded shadow-sm focus:outline-none focus:ring focus:border-emerald-400"
          />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 border rounded shadow-sm focus:outline-none focus:ring focus:border-emerald-400"
          >
            <option value="dueDate">Sort by Due Date</option>
            <option value="name">Sort by Name</option>
          </select>
          <button
            onClick={() => navigate("/midwife/clients/new")}
            className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white py-2 px-4 rounded transition-colors"
          >
            + New Client
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredClients.map((client) => (
            <div
              key={client.id}
              className="p-4 bg-white rounded-xl shadow-md hover:shadow-lg transition cursor-pointer border border-gray-100 hover:bg-emerald-50"
              onClick={() => navigate(`/charting/${client.id}`)}
            >
              <h3 className="text-lg font-semibold text-gray-800">{client.fullName}</h3>
              <p className="text-sm text-gray-600"> <span className="font-medium">{client.dateOfBirth?.slice(0, 10)}</span></p>
            </div>
          ))}
        </div>
      </div>
    </Sidebar>
  );
}
